import React from "react";
import styled from "styled-components";
import { Plus, Minus, X } from "lucide-react";
import { useLanguage } from "../contexts/language";
import { useOrder } from "../contexts/order";
import { unmodule } from "../util";

const fallbackMenuImage = unmodule(require("../assets/hebe-logo-vertical.png"));

const ItemContainer = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.2s ease;
  opacity: ${(props) =>
    props.$isOutOfStock || props.$isKitchenClosed ? 0.6 : 1};
  pointer-events: ${(props) =>
    props.$isOutOfStock || props.$isKitchenClosed ? "none" : "auto"};

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ImageWrapper = styled.div`
  width: 88px;
  height: 88px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  background-color: #f0f0f0;
  cursor: pointer;

  @media (max-width: 719px) {
    width: 72px;
    height: 72px;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const ItemDescriptionDesktop = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 8px 0;
  line-height: 1.4;

  @media (max-width: 719px) {
    display: none;
  }
`;

const ItemDescriptionMobile = styled.p`
  display: none;
  font-size: 12px;
  color: #666;
  margin: 8px 0 0 0;
  line-height: 1.4;

  @media (max-width: 719px) {
    display: block;
  }
`;

const StockInfo = styled.p`
  font-size: 12px;
  color: ${(props) => (props.$isOutOfStock ? "#999" : "#c9a961")};
  margin: 0;
  font-weight: 500;
`;

const PriceActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const Price = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #c9a961;

  @media (max-width: 719px) {
    font-size: 14px;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 2px;
`;

const QuantityButton = styled.button`
  padding: 8px;
  min-width: 36px;
  min-height: 36px;
  border: none;
  background: none;
  cursor: pointer;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.15s;

  @media (max-width: 719px) {
    padding: 6px;
    min-width: 30px;
    min-height: 30px;
  }

  &:hover {
    background-color: #e0e0e0;
  }

  &:active {
    background-color: #d5d5d5;
  }
`;

const QuantityDisplay = styled.span`
  width: 24px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const ZoomModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  padding: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
`;

const ZoomedImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const DisclaimerText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  text-align: center;
  margin-top: 12px;
  font-style: italic;
`;

export const MenuItem = React.memo(
  ({ item, isOutOfStock = false, isKitchenClosed = false }) => {
    const { t } = useLanguage();
    const { cart, updateCartQuantity, updateCartItemNote } = useOrder();
    const [showZoom, setShowZoom] = React.useState(false);
    const cartItem = cart.find((cartLine) => cartLine.id === item.id);
    const quantity = cartItem?.quantity || 0;
    const cartNote = cartItem?.note || "";

    const [localNote, setLocalNote] = React.useState(cartNote);
    const debounceRef = React.useRef(null);

    React.useEffect(() => {
      if (cartNote !== localNote && !debounceRef.current) {
        setLocalNote(cartNote);
      }
    }, [cartNote]);

    const increaseQuantity = () => {
      if (quantity >= item.stock) return;
      updateCartQuantity(item.id, quantity + 1, item);
    };

    const decreaseQuantity = () => {
      updateCartQuantity(item.id, Math.max(0, quantity - 1));
    };

    const handleNoteChange = (event) => {
      const value = event.target.value;
      setLocalNote(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateCartItemNote(item.id, value);
        debounceRef.current = null;
      }, 300);
    };

    return (
      <>
        <ItemContainer
          $isOutOfStock={isOutOfStock}
          $isKitchenClosed={isKitchenClosed}
        >
          <TopRow>
            <ImageWrapper onClick={() => setShowZoom(true)}>
              <ItemImage
                src={item.image || fallbackMenuImage}
                alt={item.name}
              />
            </ImageWrapper>

            <ItemInfo>
              <ItemName>{item.name}</ItemName>
              <ItemDescriptionDesktop>
                {item.description}
              </ItemDescriptionDesktop>
              <StockInfo $isOutOfStock={isOutOfStock}>
                {isOutOfStock
                  ? t.order.outOfStock
                  : `${item.stock} ${t.order.available}`}
              </StockInfo>
              {!isOutOfStock && !isKitchenClosed && quantity > 0 && (
                <textarea
                  value={localNote}
                  onChange={handleNoteChange}
                  maxLength={200}
                  placeholder={t.order.itemNotePlaceholder}
                  style={{
                    marginTop: "8px",
                    width: "100%",
                    minHeight: "54px",
                    border: "1px solid #e5e5e5",
                    borderRadius: "4px",
                    padding: "6px 8px",
                    fontSize: "12px",
                    color: "#555",
                    resize: "vertical",
                  }}
                />
              )}
            </ItemInfo>

            <PriceActions>
              <Price>{item?.price?.formattedText || "Rp0"}</Price>

              {!isOutOfStock && !isKitchenClosed && (
                <ActionsWrapper>
                  <QuantityControl>
                    <QuantityButton onClick={decreaseQuantity}>
                      <Minus size={14} />
                    </QuantityButton>
                    <QuantityDisplay>{quantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={increaseQuantity}
                      style={
                        quantity >= item.stock
                          ? { opacity: 0.3, cursor: "not-allowed" }
                          : {}
                      }
                    >
                      <Plus size={14} />
                    </QuantityButton>
                  </QuantityControl>
                </ActionsWrapper>
              )}
            </PriceActions>
          </TopRow>

          <ItemDescriptionMobile>{item.description}</ItemDescriptionMobile>
        </ItemContainer>

        {showZoom && (
          <ZoomModal onClick={() => setShowZoom(false)}>
            <CloseButton onClick={() => setShowZoom(false)}>
              <X size={24} />
            </CloseButton>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ textAlign: "center" }}
            >
              <ZoomedImage
                src={item.image || fallbackMenuImage}
                alt={item.name}
              />
              <DisclaimerText>
                *Gambar hanya ilustrasi, bukan foto asli produk
              </DisclaimerText>
            </div>
          </ZoomModal>
        )}
      </>
    );
  },
);
MenuItem.displayName = "MenuItem";
